import { Round } from '@models/Match';

export const ROUND_ORDER = [
  Round.Roundof32,
  Round.Roundof16,
  Round.Quarter,
  Round.Semi,
  Round.Third,
  Round.Final,
];

const FASE_TO_START_ROUND = {
  '16avos': Round.Roundof32,
  '8vos': Round.Roundof16,
  '4tos': Round.Quarter,
  semis: Round.Semi,
  third: Round.Third,
};

export const getStartRoundForFase = (fase, hasRoundOf32 = true) => {
  if (fase === '16avos' && !hasRoundOf32) {
    return Round.Roundof16;
  }
  return FASE_TO_START_ROUND[fase] || Round.Roundof16;
};

export const isRoundAtOrAfter = (round, startRound) => {
  const roundIdx = ROUND_ORDER.indexOf(round);
  const startIdx = ROUND_ORDER.indexOf(startRound);
  if (roundIdx === -1 || startIdx === -1) return false;
  return roundIdx >= startIdx;
};

/**
 * Scope del comodín por fase de activación:
 *
 * - 16avos / 8vos / 4tos / semis: rondas AT or AFTER startRound, incluyendo
 *   3er puesto Y FINAL (regla general "desde la activación en adelante").
 * - third: SOLO el 3er puesto. El partido de la final queda intacto porque
 *   ya está "definido" por las semis y no se considera dentro del alcance
 *   de un comodín activado tan tarde.
 * - final: bloqueada (no activable; manejado por `canActivateComodin`).
 */
export const isRoundInWildcardScope = (round, startRound) => {
  if (!round || !startRound) return false;
  if (startRound === Round.Third) return round === Round.Third;
  return isRoundAtOrAfter(round, startRound);
};

export const isRoundAfter = (round, startRound) => {
  const roundIdx = ROUND_ORDER.indexOf(round);
  const startIdx = ROUND_ORDER.indexOf(startRound);
  if (roundIdx === -1 || startIdx === -1) return false;
  return roundIdx > startIdx;
};

export const isRoundBefore = (round, startRound) => {
  const roundIdx = ROUND_ORDER.indexOf(round);
  const startIdx = ROUND_ORDER.indexOf(startRound);
  if (roundIdx === -1 || startIdx === -1) return false;
  return roundIdx < startIdx;
};

/** Quita equipos del forecast en fases posteriores para que la propagación calcule ganadores. */
export const stripForecastTeamsForPropagation = (
  forecast,
  midToRound,
  startRound,
  enabled
) => {
  if (!enabled || !startRound) return forecast || [];

  return (forecast || []).map((row) => {
    if (!row?.mid) return row;
    const round = midToRound[row.mid];
    if (!round || !isRoundAfter(round, startRound)) return row;

    const { teamHome, teamAway, ...rest } = row;
    return rest;
  });
};

/** Elimina datos obsoletos del pronóstico original en fases posteriores al comodín. */
export const stripStaleForecastAfterComodin = (
  forecast,
  midToRound,
  startRound,
  enabled
) => {
  if (!enabled || !startRound) return forecast || [];

  return (forecast || []).map((row) => {
    if (!row?.mid) return row;
    const round = midToRound[row.mid];
    if (!round || !isRoundAfter(round, startRound)) return row;
    if (
      row.modificadoPorComodin ||
      row.scoreHome != null ||
      row.scoreAway != null ||
      (row.winner != null && row.winner !== '')
    ) {
      return row;
    }

    const { teamHome, teamAway, scoreHome, scoreAway, winner, ...rest } = row;
    return rest;
  });
};

export const getComodinStartRound = (forecast, midToRound) => {
  const rounds = (forecast || [])
    .filter((row) => row && row.modificadoPorComodin && row.mid != null)
    .map((row) => midToRound[row.mid])
    .filter(Boolean);

  if (rounds.length === 0) return null;

  return rounds.reduce(
    (earliest, round) =>
      !earliest || ROUND_ORDER.indexOf(round) < ROUND_ORDER.indexOf(earliest)
        ? round
        : earliest,
    null
  );
};

export const hasComodinAtOrAfterRound = (forecast, midToRound, fromRound) =>
  (forecast || []).some((row) => {
    if (!row?.modificadoPorComodin) return false;
    const round = midToRound[row.mid];
    return round && isRoundAtOrAfter(round, fromRound);
  });

export const buildBracketMidToRound = (matches = []) =>
  matches
    .filter((m) => m && m.stage === 'bracket' && m.mid != null)
    .reduce((all, m) => ({ ...all, [m.mid]: m.round }), {});

/** Solo fases eliminatorias desde la ronda de activación del comodín (hacia adelante), excluyendo la fase bloqueada (final). */
export const isForecastMidInWildcardScope = (
  mid,
  faseActivacion,
  matches = [],
  hasRoundOf32 = true
) => {
  if (mid == null || !faseActivacion) return false;
  const startRound = getStartRoundForFase(faseActivacion, hasRoundOf32);
  const round = buildBracketMidToRound(matches)[mid];
  return Boolean(round && isRoundInWildcardScope(round, startRound));
};

/** Filtra pronósticos dirty/patches: nunca envía fases anteriores a la activación. */
export const filterForecastRowsForWildcard = (
  rows,
  { faseActivacion, matches = [], hasRoundOf32 = true }
) => {
  if (!faseActivacion) return rows || [];
  return (rows || []).filter((row) =>
    isForecastMidInWildcardScope(row.mid, faseActivacion, matches, hasRoundOf32)
  );
};
